import React, { useState, useEffect, useRef } from 'react';
import SVGLogo from './SVGLogo';
import AbstractPortfolio from './AbstractPortfolio';

export default function App() {
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [hoveredSphere, setHoveredSphere] = useState(null);
  const [mouseData, setMouseData] = useState({ x: 0, y: 0, distance: 0, angle: 0 });
  const [showOrbitalNav, setShowOrbitalNav] = useState(true);
  const [currentSection, setCurrentSection] = useState('landing');
  const totalFrames = 360; // Using 360 for smooth rotation (degrees)
  
  const targetFrameRef = useRef(0);
  const currentFrameFloatRef = useRef(0);
  const velocityRef = useRef(0);
  const animationFrameRef = useRef(null);
  
  const SETTINGS = {
    ease: 0.12,
    friction: 0.85,
    maxVelocity: 3,
    threshold: 0.05
  };
  
  // Orbital navigation spheres
  const spheres = [
    { id: 'portfolio', label: 'Portfolio', angle: 0, color: 'cyan', href: '#portfolio' },
    { id: 'about', label: 'About', angle: -50, color: 'orange', href: '#about' },
    { id: 'contact', label: 'Contact', angle: 50, color: 'cyan', href: '#contact' },
  ];
  
  // Calculate orbital position
  const getOrbitalPosition = (angle, radius = 340) => {
    const rad = (angle - 90) * (Math.PI / 180);
    return {
      left: `calc(50% + ${Math.cos(rad) * radius}px)`,
      top: `calc(50% + ${Math.sin(rad) * radius}px)`
    };
  };
  
  // Smooth animation loop
  useEffect(() => {
    let lastTime = performance.now();
    
    const smoothStep = (currentTime) => {
      const deltaTime = Math.min((currentTime - lastTime) / 16.67, 2);
      lastTime = currentTime;
      
      const current = currentFrameFloatRef.current;
      const target = targetFrameRef.current;
      let diff = target - current;
      
      if (Math.abs(diff) > totalFrames / 2) {
        diff = diff > 0 ? diff - totalFrames : diff + totalFrames;
      }
      
      const force = diff * SETTINGS.ease;
      velocityRef.current = (velocityRef.current * SETTINGS.friction + force) * deltaTime;
      velocityRef.current = Math.max(-SETTINGS.maxVelocity, Math.min(SETTINGS.maxVelocity, velocityRef.current));
      
      let newFrame = current + velocityRef.current;
      if (newFrame < 0) newFrame += totalFrames;
      if (newFrame >= totalFrames) newFrame -= totalFrames;
      
      currentFrameFloatRef.current = newFrame;
      setCurrentFrame(Math.floor(newFrame));
      
      if (Math.abs(diff) > SETTINGS.threshold || Math.abs(velocityRef.current) > SETTINGS.threshold) {
        animationFrameRef.current = requestAnimationFrame(smoothStep);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(smoothStep);
    return () => animationFrameRef.current && cancelAnimationFrame(animationFrameRef.current);
  }, []);
  
  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!logoRef.current) return;
      
      // Get the actual center position of the logo on screen
      const logoRect = logoRef.current.getBoundingClientRect();
      const centerX = logoRect.left + logoRect.width / 2;
      const centerY = logoRect.top + logoRect.height / 2;
      
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;
      
      let angle = Math.atan2(mouseX, -mouseY) * (180 / Math.PI);
      if (angle < 0) angle += 360;
      
      targetFrameRef.current = (angle / 360) * totalFrames;
      
      // Calculate distance (normalized 0-1)
      const maxDistance = Math.sqrt(window.innerWidth * window.innerWidth + window.innerHeight * window.innerHeight) / 2;
      const distance = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
      const normalizedDistance = Math.min(distance / maxDistance, 1);
      
      // Normalize mouse position (-1 to 1)
      const normalizedX = (mouseX / (window.innerWidth / 2)) * 0.3; // Limit movement to 30%
      const normalizedY = (mouseY / (window.innerHeight / 2)) * 0.3;
      
      setMouseData({
        x: normalizedX,
        y: normalizedY,
        distance: normalizedDistance,
        angle: angle
      });
      
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {});
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Scroll detection - hide orbital nav when scrolled away from landing
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      setShowOrbitalNav(scrolled < 100);
      
      // Detect current section
      const sections = ['landing', 'portfolio', 'about', 'contact'];
      const windowHeight = window.innerHeight;
      
      for (let section of sections) {
        if (section === 'landing' && scrolled < windowHeight / 2) {
          setCurrentSection('landing');
          break;
        }
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            setCurrentSection(section);
            break;
          }
        }
      }
    };
    
    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
    {/* Landing Section */}
    <div 
      ref={containerRef}
      className="w-full h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center overflow-hidden relative"
    >
      {/* Navigation Spheres - Orbital - fade out when scrolled */}
      <div 
        className="transition-opacity duration-500"
        style={{ opacity: showOrbitalNav ? 1 : 0, pointerEvents: showOrbitalNav ? 'auto' : 'none' }}
      >
        {spheres.map((sphere) => {
          const position = getOrbitalPosition(sphere.angle);
          return (
            <a
              key={sphere.id}
              href={sphere.href}
            className="fixed z-40 group cursor-pointer no-underline"
            style={{
              ...position,
              transform: 'translate(-50%, -50%)',
              width: '100px', // Stable hit area
              height: '100px', // Stable hit area
            }}
            onMouseEnter={() => setHoveredSphere(sphere.id)}
            onMouseLeave={() => setHoveredSphere(null)}
          >
            {/* Invisible stable hit area - prevents flicker */}
            <div 
              className="absolute inset-0"
              style={{
                width: '100px',
                height: '100px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
            
            {/* Connection line to center */}
            <div 
              className={`absolute w-px transition-all duration-500 pointer-events-none ${
                sphere.color === 'cyan' ? 'bg-cyan-500' : 'bg-orange-500'
              }`}
              style={{
                height: hoveredSphere === sphere.id ? '340px' : '0px',
                opacity: hoveredSphere === sphere.id ? 0.2 : 0,
                bottom: '50%',
                left: '50%',
                transform: `rotate(${sphere.angle + 90}deg)`,
                transformOrigin: 'bottom center'
              }}
            />
            
            {/* Glow */}
            <div 
              className={`absolute rounded-full blur-xl transition-all duration-500 pointer-events-none ${
                sphere.color === 'cyan' ? 'bg-cyan-500' : 'bg-orange-500'
              }`}
              style={{
                width: hoveredSphere === sphere.id ? '100px' : '30px',
                height: hoveredSphere === sphere.id ? '100px' : '30px',
                opacity: hoveredSphere === sphere.id ? 0.5 : 0.3,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                position: 'absolute'
              }}
            />
            
            {/* Sphere - centered in hit area */}
            <div 
              className={`absolute rounded-full backdrop-blur-sm transition-all duration-500 flex items-center justify-center pointer-events-none ${
                sphere.color === 'cyan' 
                  ? 'bg-cyan-500/10 border-cyan-500' 
                  : 'bg-orange-500/10 border-orange-500'
              }`}
              style={{
                width: hoveredSphere === sphere.id ? '80px' : '24px',
                height: hoveredSphere === sphere.id ? '80px' : '24px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderWidth: hoveredSphere === sphere.id ? '2px' : '1px',
                boxShadow: hoveredSphere === sphere.id 
                  ? sphere.color === 'cyan'
                    ? '0 0 30px rgba(0, 245, 255, 0.4)'
                    : '0 0 30px rgba(255, 107, 53, 0.4)'
                  : 'none'
              }}
            >
              {/* Pulse ring on hover */}
              {hoveredSphere === sphere.id && (
                <div 
                  className={`absolute inset-0 rounded-full border-2 animate-ping pointer-events-none ${
                    sphere.color === 'cyan' ? 'border-cyan-500' : 'border-orange-500'
                  }`}
                  style={{ animationDuration: '2s' }}
                />
              )}
              
              {/* Icon/indicator */}
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-300 pointer-events-none ${
                  sphere.color === 'cyan' ? 'bg-cyan-400' : 'bg-orange-400'
                }`}
                style={{
                  width: hoveredSphere === sphere.id ? '12px' : '6px',
                  height: hoveredSphere === sphere.id ? '12px' : '6px',
                }}
              />
            </div>
            
            {/* Label */}
            <div 
              className={`absolute top-full mt-4 left-1/2 transform -translate-x-1/2 transition-all duration-300 whitespace-nowrap text-sm font-medium tracking-wider ${
                sphere.color === 'cyan' ? 'text-cyan-400' : 'text-orange-400'
              }`}
              style={{
                opacity: hoveredSphere === sphere.id ? 1 : 0,
                transform: hoveredSphere === sphere.id 
                  ? 'translate(-50%, 0)' 
                  : 'translate(-50%, -10px)'
              }}
            >
              {sphere.label}
            </div>
          </a>
        );
      })}
      </div>
      
      {/* Background */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `
          radial-gradient(circle at center, rgba(0, 245, 255, 0.05) 0%, transparent 70%),
          linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)`,
        backgroundSize: 'cover, 100px 100px, 100px 100px'
      }} />
      
      {/* Orbital ring guide */}
      <div className="absolute z-0" style={{
        width: '560px',
        height: '560px',
        borderRadius: '50%',
        border: '1px solid rgba(0, 245, 255, 0.05)'
      }} />
      
      {/* Main content - centered absolutely to match orbital spheres */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center">
        
        {/* Logo */}
        <div className="relative group overflow-visible">
          <div className="absolute inset-0 blur-3xl opacity-30 group-hover:opacity-40 transition-opacity duration-700 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-transparent rounded-full animate-pulse" />
            <div className="absolute inset-0 bg-gradient-to-l from-orange-500 to-transparent rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <div 
            ref={logoRef}
            className="relative w-[36rem] h-[36rem] transition-transform duration-200 ease-out overflow-visible flex items-center justify-center"
            style={{
              // Limit vertical movement to prevent overlap with text
              transform: `translate(${mouseData.x * 60}px, ${Math.max(mouseData.y * 60, -40)}px)`
            }}
          >
            <div className="w-[28rem] h-[28rem]">
              <SVGLogo 
                rotation={(currentFrame / totalFrames) * 360}
                intensity={Math.abs(Math.sin((currentFrame / totalFrames) * Math.PI * 2)) * 0.5 + 0.5}
                mouseX={mouseData.x}
                mouseY={mouseData.y}
                distance={mouseData.distance}
              />
            </div>
          </div>
          
          {/* Progress indicator */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none opacity-20"
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle
              cx="50%"
              cy="50%"
              r="49.5%"
              fill="none"
              stroke="rgba(0, 245, 255, 0.4)"
              strokeWidth="1"
              strokeDasharray={`${(currentFrameFloatRef.current / totalFrames) * 100} 100`}
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        {/* Name - positioned absolutely below center */}
        <div className="absolute top-[22rem] text-center space-y-2 z-20 whitespace-nowrap">
          <h1 className="text-7xl font-bold text-white tracking-tight">
            DARYL KIRCHEN
          </h1>
          <div className="h-px w-20 mx-auto bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-40" />
          <p className="text-base text-cyan-400/70 font-light tracking-[0.5em] uppercase">
            Interactive Developer
          </p>
        </div>
      </div>
      
      {/* Hint */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-slate-700 text-xs font-mono">
        Move to explore • Hover spheres to navigate
      </div>
      
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: '2px',
              height: '2px',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 2 === 0 ? 'rgba(0, 245, 255, 0.15)' : 'rgba(255, 107, 53, 0.15)',
              animation: `float ${12 + Math.random() * 20}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0); opacity: 0; }
          50% { transform: translate(${Math.random() * 40 - 20}px, -${Math.random() * 50 + 25}px); opacity: 0.5; }
        }
      `}</style>
    </div>
    
    {/* Portfolio Section */}
    <div id="portfolio" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
      <AbstractPortfolio />
      
      {/* Back to Home - only in portfolio section */}
      <a 
        href="#"
        className="fixed top-8 left-8 z-[70] text-cyan-400/50 hover:text-cyan-400 transition-all duration-300 text-sm font-mono flex items-center gap-2 opacity-0 animate-fade-in"
        style={{ 
          animationDelay: '500ms',
          animationFillMode: 'forwards'
        }}
      >
        <span className="text-xl">←</span>
        <span>Home</span>
      </a>
    </div>
    
    {/* About Section */}
    <div id="about" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 flex items-center justify-center relative">
      <div className="text-center">
        <h2 className="text-5xl font-bold text-white mb-4">About</h2>
        <p className="text-cyan-400/70">Coming soon...</p>
      </div>
    </div>
    
    {/* Contact Section */}
    <div id="contact" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center relative">
      <div className="text-center">
        <h2 className="text-5xl font-bold text-white mb-4">Contact</h2>
        <p className="text-cyan-400/70">Coming soon...</p>
      </div>
    </div>
    
    {/* Side Navigation - appears when scrolled away from landing, hidden in portfolio */}
    <div 
      className="fixed left-8 top-1/2 -translate-y-1/2 z-[60] flex flex-col gap-6 transition-opacity duration-500"
      style={{ 
        opacity: (showOrbitalNav || currentSection === 'portfolio') ? 0 : 1, 
        pointerEvents: (showOrbitalNav || currentSection === 'portfolio') ? 'none' : 'auto' 
      }}
    >
      {[
        { id: 'landing', label: 'Home', href: '#' },
        { id: 'portfolio', label: 'Portfolio', href: '#portfolio' },
        { id: 'about', label: 'About', href: '#about' },
        { id: 'contact', label: 'Contact', href: '#contact' }
      ].map((section) => (
        <a
          key={section.id}
          href={section.href}
          className="group relative flex items-center"
        >
          {/* Dot */}
          <div 
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSection === section.id 
                ? 'bg-cyan-400 scale-125' 
                : 'bg-cyan-400/30 hover:bg-cyan-400/60'
            }`}
          />
          
          {/* Label - appears on hover */}
          <span 
            className="absolute left-full ml-4 text-sm font-mono text-cyan-400/70 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            {section.label}
          </span>
        </a>
      ))}
    </div>
    </>
  );
}
